  // src/pages/admin-audit-management/components/PendingAuditsTable.jsx
  import React, { useState } from 'react';
  import Icon from '../../../components/AppIcon';
  import Image from '../../../components/AppImage';
  import Button from '../../../components/ui/Button';
  import { supabaseAdmin } from '../../../lib/supabase';
  import { auditReportService } from '../../../services/auditReportService';
  import { storageService } from '../../../services/storageService';
  import toast from 'react-hot-toast';

  // ─── Status normaliser ────────────────────────────────────────────────────────
  const normalisePendingStatus = (status) => {
    const done = ['published', 'completed'];
    if (done.includes(status?.toLowerCase())) return status;
    return 'pending';
  };

  // ─── Pending status pill ──────────────────────────────────────────────────────
  const PendingPill = () => (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '5px 12px',
      borderRadius: '20px',
      background: '#f1f5f9',
      border: '1px solid #e2e8f0',
      fontSize: '13px',
      fontWeight: 500,
      color: '#64748b',
      whiteSpace: 'nowrap',
    }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
      Pending
    </span>
  );

  // ─── Payment Status pill ──────────────────────────────────────────────────────
  const PaymentPill = ({ status, auditType }) => {
    const isFree     = auditType === 'free';
    const isReceived = !isFree && (status === 'captured' || status === 'received');

    if (isFree) return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        No Payment
      </span>
    );

    if (isReceived) return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', background: '#dcfce7', border: '1px solid #86efac', color: '#166534' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        Received
      </span>
    );

    // Everything else (pending, failed, cancelled, null) = Failed
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        Failed
      </span>
    );
  };

  // ─── Plan badge — matches screenshot exactly ──────────────────────────────────
  const PlanBadge = ({ type }) => {
    const isPaid = type === 'paid';
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        marginTop: '4px',
        ...(isPaid
          ? { background: 'rgba(34,197,94,0.1)', color: 'rgb(22,163,74)', border: '1px solid rgba(34,197,94,0.25)' }
          : { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }),
      }}>
        {isPaid ? (
          /* Exact Lucide Crown icon */
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 6.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.962.735H5.815a1 1 0 0 1-.962-.735L2.02 7.019a.5.5 0 0 1 .798-.519l4.276 2.664a1 1 0 0 0 1.516-.294z"/>
            <path d="M5 21h14"/>
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        )}
        {isPaid ? 'Paid Audit' : 'Free Audit'}
      </span>
    );
  };

  // ─── Sort button ──────────────────────────────────────────────────────────────
  const SortBtn = ({ field, label, sortField, sortDirection, onSort }) => (
    <button
      onClick={() => onSort(field)}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '13px', fontWeight: 500, color: '#374151',
        background: 'none', border: 'none', cursor: 'pointer',
        padding: 0, whiteSpace: 'nowrap',
      }}
    >
      {label}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ opacity: sortField === field ? 1 : 0.4 }}>
        {sortField === field && sortDirection === 'asc'
          ? <path d="m18 15-6-6-6 6" />
          : sortField === field && sortDirection === 'desc'
            ? <path d="m6 9 6 6 6-6" />
            : <><path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" /></>
        }
      </svg>
    </button>
  );

  // ─── Date formatter with IST timezone ────────────────────────────────────────
  const fmtDateIST = (d) => {
    if (!d) return { date: 'N/A', time: '', tz: '' };
    const dt = new Date(d);
    const date = dt.toLocaleDateString('en-IN', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
    const time = dt.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Asia/Kolkata',
    });
    return { date, time, tz: 'IST' };
  };

  // ─── Main component ───────────────────────────────────────────────────────────
  const PendingAuditsTable = ({ audits = [], onViewDetails, onAuditDeleted }) => {
    const [sortField,       setSortField]       = useState('submissionDate');
    const [sortDirection,   setSortDirection]   = useState('desc');
    const [expandedRows,    setExpandedRows]    = useState(new Set());
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [deletingId,      setDeletingId]      = useState(null);
    const [downloadingIds,  setDownloadingIds]  = useState(new Set());

    const handleSort = (field) => {
      if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
      else { setSortField(field); setSortDirection('desc'); }
    };

    const sortedAudits = [...audits].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (['submissionDate', 'registrationDate'].includes(sortField)) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

    const toggleRow = (id) => {
      setExpandedRows(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    };

    const handleDelete = async (audit) => {
      try {
        setDeletingId(audit.id);
        if (audit._sourceTable === 'deck_submissions') {
          const { error } = await supabaseAdmin
            .from('deck_submissions').delete().eq('id', audit._sourceId);
          if (error) throw error;
        } else {
          await auditReportService.deleteAuditReport(audit._sourceId);
        }
        toast.success('Deleted successfully', { icon: '🗑️' });
        setDeleteConfirmId(null);
        onAuditDeleted?.();
      } catch (err) {
        toast.error('Failed to delete: ' + (err.message || 'Unknown error'));
      } finally {
        setDeletingId(null);
      }
    };

    const handleDownload = async (audit) => {
      if (!audit.fileUrl) { toast.error('No file available'); return; }
      if (audit.fileUrl.startsWith('http')) { window.open(audit.fileUrl, '_blank'); return; }
      try {
        setDownloadingIds(prev => new Set(prev).add(audit.id));
        const url = await storageService.getDeckFileSignedUrl(audit.fileUrl);
        window.open(url, '_blank');
        toast.success('Download started', { icon: '📥' });
      } catch {
        toast.error('Failed to download. Please try again.');
      } finally {
        setDownloadingIds(prev => { const n = new Set(prev); n.delete(audit.id); return n; });
      }
    };

    const fmtFull = (d) => d
      ? new Date(d).toLocaleString('en-IN', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
          timeZone: 'Asia/Kolkata',
        }) + ' IST'
      : 'N/A';

    if (audits.length === 0) {
      return (
        <div style={{
          background: '#fff', borderRadius: '12px',
          border: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '48px 24px', textAlign: 'center',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <Icon name="CheckCircle2" size={24} style={{ color: '#16a34a' }} />
          </div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: '0 0 6px' }}>
            All Caught Up!
          </p>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0, maxWidth: 360 }}>
            No pending audits at the moment. New submissions will appear here.
          </p>
        </div>
      );
    }

    const TH = { padding: '10px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' };

    return (
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1050px' }}>

            {/* ── HEADER ────────────────────────────────────────────────── */}
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ ...TH, width: '44px', padding: '10px 8px 10px 16px' }} />

                <th style={TH}>
                  <SortBtn field="userName" label="User"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                <th style={TH}>
                  <SortBtn field="requestId" label="Request ID"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                {/* ── NEW: Payment Status ── */}
                <th style={TH}>
                  <SortBtn field="paymentStatus" label="Payment Status"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                <th style={TH}>
                  <SortBtn field="presentationTitle" label="Presentation"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                {/* ── RENAMED: Report Request Date ── */}
                <th style={TH}>
                  <SortBtn field="registrationDate" label="Report Request Date"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                <th style={TH}>
                  <SortBtn field="status" label="Status"
                    sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                </th>

                <th style={{ ...TH, textAlign: 'right' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>Actions</span>
                </th>
              </tr>
            </thead>

            {/* ── BODY ──────────────────────────────────────────────────── */}
            <tbody>
              {sortedAudits.map((audit, idx) => {
                const isExpanded    = expandedRows.has(audit.id);
                const hasHtmlReport = !!audit.htmlUrl;
                const isDelConfirm  = deleteConfirmId === audit.id;
                const isDeleting    = deletingId === audit.id;
                const isDownloading = downloadingIds.has(audit.id);
                const isLast        = idx === sortedAudits.length - 1;
                const { date: reqDate, time: reqTime, tz: reqTz } = fmtDateIST(audit.registrationDate);

                return (
                  <React.Fragment key={audit.id}>
                    <tr style={{
                      borderBottom: isExpanded ? 'none' : isLast ? 'none' : '1px solid #f1f5f9',
                      background: '#fff',
                      transition: 'background 0.1s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      {/* Expand chevron */}
                      <td style={{ padding: '16px 4px 16px 16px', width: '44px', verticalAlign: 'middle' }}>
                        <button
                          onClick={() => toggleRow(audit.id)}
                          style={{
                            width: 24, height: 24,
                            background: 'none', border: 'none',
                            cursor: 'pointer', padding: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#94a3b8', borderRadius: '4px',
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#374151'}
                          onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            {isExpanded
                              ? <path d="m6 9 6 6 6-6" />
                              : <path d="m9 18 6-6-6-6" />
                            }
                          </svg>
                        </button>
                      </td>

                      {/* User */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle', minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <img
                            src={audit.userAvatar}
                            alt={audit.userAvatarAlt}
                            style={{
                              width: 40, height: 40,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              flexShrink: 0,
                              border: '2px solid #f1f5f9',
                            }}
                            onError={e => { e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(audit.userName || 'U') + '&background=e2e8f0&color=64748b'; }}
                          />
                          <div>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                              {audit.userName}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                              {audit.userEmail}
                            </p>
                            <PlanBadge type={audit.auditType} />
                          </div>
                        </div>
                      </td>

                      {/* Request ID */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle', minWidth: '180px' }}>
                        <p style={{
                          margin: 0,
                          fontSize: '14px', fontWeight: 600,
                          color: '#3b82f6',
                          fontFamily: 'ui-monospace, monospace',
                          letterSpacing: '-0.01em',
                        }}>
                          {audit.requestId || 'N/A'}
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                          Audit Type: {audit.auditType === 'paid' ? 'PAID' : 'FREE'}
                        </p>
                      </td>

                      {/* ── Payment Status ── */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle', minWidth: '190px' }}>
                        <PaymentPill status={audit.paymentStatus} auditType={audit.auditType} />
                        {audit.razorpayPaymentId && (
                          <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#94a3b8', fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.01em' }}>
                            {audit.razorpayPaymentId}
                          </p>
                        )}
                      </td>

                      {/* Presentation */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle', minWidth: '200px' }}>
                        <p style={{
                          margin: 0,
                          fontSize: '14px', fontWeight: 600, color: '#0f172a',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          maxWidth: '220px',
                        }}>
                          {audit.presentationTitle}
                        </p>
                        {audit.deckFileName && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                              stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                            </svg>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{audit.deckFileName}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                          {audit.fileType && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              padding: '2px 7px', borderRadius: '4px',
                              background: '#f1f5f9', border: '1px solid #e2e8f0',
                              fontSize: '11px', fontWeight: 500, color: '#64748b',
                            }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                              {audit.fileType}
                            </span>
                          )}
                          {audit.slideCount > 0 && (
                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                              {audit.slideCount} slides
                            </span>
                          )}
                        </div>
                      </td>

                      {/* ── Report Request Date (was Registered) with IST ── */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle', minWidth: '160px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            {reqDate}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>{reqTime}</span>
                          <span style={{
                            fontSize: '10px', fontWeight: 600,
                            color: '#0082FB',
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '3px',
                            padding: '1px 6px',
                            letterSpacing: '0.04em',
                            lineHeight: '18px',
                          }}>
                            {reqTz}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                        <PendingPill />
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>

                          {audit.fileUrl && (
                            <button
                              onClick={() => handleDownload(audit)}
                              disabled={isDownloading}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: '7px',
                                border: '1px solid #e2e8f0', background: '#fff',
                                fontSize: '13px', fontWeight: 500, color: '#374151',
                                cursor: isDownloading ? 'not-allowed' : 'pointer',
                                opacity: isDownloading ? 0.6 : 1,
                                transition: 'background 0.15s, border-color 0.15s',
                              }}
                              onMouseEnter={e => { if (!isDownloading) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                              Deck
                            </button>
                          )}

                          <button
                            onClick={() => hasHtmlReport && onViewDetails?.(audit)}
                            disabled={!hasHtmlReport}
                            title={hasHtmlReport ? 'View HTML report' : 'Report not yet assigned'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '6px 12px', borderRadius: '7px',
                              border: '1px solid #e2e8f0',
                              background: hasHtmlReport ? '#fff' : '#f8fafc',
                              fontSize: '13px', fontWeight: 500,
                              color: hasHtmlReport ? '#374151' : '#cbd5e1',
                              cursor: hasHtmlReport ? 'pointer' : 'not-allowed',
                              opacity: hasHtmlReport ? 1 : 0.5,
                              transition: 'background 0.15s, border-color 0.15s',
                            }}
                            onMouseEnter={e => { if (hasHtmlReport) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                            onMouseLeave={e => { if (hasHtmlReport) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                            View
                          </button>

                          {isDelConfirm ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <button
                                onClick={() => handleDelete(audit)}
                                disabled={isDeleting}
                                style={{
                                  padding: '6px 10px', borderRadius: '7px',
                                  border: '1px solid #fca5a5', background: '#fef2f2',
                                  fontSize: '12px', fontWeight: 600, color: '#dc2626',
                                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                                  opacity: isDeleting ? 0.6 : 1,
                                }}
                              >
                                {isDeleting ? '...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                disabled={isDeleting}
                                style={{
                                  width: 28, height: 28, borderRadius: '6px',
                                  border: '1px solid #e2e8f0', background: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', color: '#94a3b8',
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                  <line x1="18" y1="6" x2="6" y2="18"/>
                                  <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(audit.id)}
                              title="Delete submission"
                              style={{
                                width: 30, height: 30, borderRadius: '7px',
                                border: 'none', background: 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#cbd5e1',
                                transition: 'color 0.15s, background 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6"/>
                                <path d="M9 6V4h6v2"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* ── EXPANDED DETAIL ROW ────────────────────────────── */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{
                          padding: '0 16px 16px 56px',
                          background: '#f8fafc',
                          borderBottom: isLast ? 'none' : '1px solid #e2e8f0',
                        }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '16px 24px',
                            padding: '16px 20px',
                            background: '#fff',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                          }}>
                            <DetailField icon="hash"     label="Request ID"           value={audit.requestId} mono />
                            <DetailField icon="user"     label="Full Name"             value={audit.userName} />
                            <DetailField icon="mail"     label="Email Address"         value={audit.userEmail} />
                            <DetailField icon="shield"   label="Role"                  value={audit.userRole || 'User'} />
                            <DetailField icon="credit"   label="Subscription"          value={audit.auditType === 'paid' ? 'Paid' : 'Free'} />
                            <DetailField icon="payment"  label="Payment Status"        value={
                              audit.auditType === 'free' ? 'No Payment' :
                              (audit.paymentStatus === 'captured' || audit.paymentStatus === 'received') ? 'Received' :
                              'Failed'
                            } />
                            <DetailField icon="hash" label="Payment ID" value={
                              audit.auditType === 'free' ? '—' :
                              audit.razorpayPaymentId || '—'
                            } mono />
                            <DetailField icon="calendar" label="Report Request Date"   value={fmtFull(audit.registrationDate)} />
                            <DetailField icon="clock"    label="Submission Date"       value={fmtFull(audit.submissionDate)} />
                            <DetailField icon="file"     label="File Name"             value={audit.fileName} />
                            <DetailField icon="layers"   label="Slide Count"           value={audit.slideCount ? `${audit.slideCount} slides` : null} />
                            {audit.userId && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <DetailField icon="hash" label="User ID" value={audit.userId} mono />
                              </div>
                            )}
                            {audit.notes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Notes
                                </p>
                                <p style={{ margin: 0, fontSize: '13px', color: '#374151', background: '#f8fafc', padding: '8px 12px', borderRadius: 6 }}>
                                  {audit.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ─── Detail field ─────────────────────────────────────────────────────────────
  const DetailField = ({ label, value, mono = false }) => {
    if (!value) return null;
    return (
      <div>
        <p style={{
          margin: '0 0 3px',
          fontSize: '11px', fontWeight: 500,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {label}
        </p>
        <p style={{
          margin: 0,
          fontSize: '13px', fontWeight: 500,
          color: '#0f172a',
          wordBreak: 'break-all',
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
        }}>
          {value}
        </p>
      </div>
    );
  };

  export default PendingAuditsTable;